/**
 * Deterministic agent sigils. The same function renders avatars in the app and serves the
 * ERC-8004 `image` (functions/img/agents/[file].ts), so an agent looks the same everywhere.
 *
 * A sigil is a mirrored 7×7 glyph on a dark plate: ink cells, a few ultraviolet "cores"
 * and one acid signal cell, wrapped in HUD corner ticks.
 */

const PLATE = "#0c0e16";
const CELL = "#d9dcea";
const DIM = "#4a4f6a";
const CORE = "#7b61ff";
const SIGNAL = "#b4f000";

/** xorshift32 seeded from an arbitrary string. */
function rng(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let x = h >>> 0 || 0x9e3779b9;
  return () => {
    x ^= x << 13;
    x >>>= 0;
    x ^= x >>> 17;
    x ^= x << 5;
    x >>>= 0;
    return x / 4294967296;
  };
}

export type SigilCell = { x: number; y: number; kind: "cell" | "dim" | "core" | "signal" };

export function sigilCells(id: string | number | bigint, size = 7): SigilCell[] {
  const rand = rng(`botnet.family/agent/${id.toString()}`);
  const half = Math.ceil(size / 2);
  const cells: SigilCell[] = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < half; x++) {
      const r = rand();
      // denser towards the centre column for a more "organic" silhouette
      const bias = x === half - 1 ? 0.12 : 0;
      const kind: SigilCell["kind"] | null =
        r < 0.06 ? "core" : r < 0.42 + bias ? "cell" : r < 0.52 ? "dim" : null;
      if (!kind) continue;
      cells.push({ x, y, kind });
      if (x !== size - 1 - x) cells.push({ x: size - 1 - x, y, kind });
    }
  }
  // exactly one signal cell, on the axis of symmetry
  const sy = Math.floor(rand() * size);
  const filtered = cells.filter((c) => !(c.x === half - 1 && c.y === sy));
  filtered.push({ x: half - 1, y: sy, kind: "signal" });
  return filtered;
}

const COLORS: Record<SigilCell["kind"], string> = {
  cell: CELL,
  dim: DIM,
  core: CORE,
  signal: SIGNAL,
};

/** Standalone SVG document (used for the ERC-8004 image and as a data URI). */
export function sigilSvg(id: string | number | bigint, opts: { label?: boolean } = {}): string {
  const size = 7;
  const unit = 10;
  const pad = 18;
  const dim = size * unit + pad * 2;
  const cells = sigilCells(id, size)
    .map(
      (c) =>
        `<rect x="${pad + c.x * unit + 1}" y="${pad + c.y * unit + 1}" width="${unit - 2}" height="${unit - 2}" fill="${COLORS[c.kind]}"/>`,
    )
    .join("");
  const t = 6; // corner tick length
  const e = 6; // inset
  const ticks = [
    `M${e} ${e + t}V${e}H${e + t}`,
    `M${dim - e - t} ${e}H${dim - e}V${e + t}`,
    `M${e} ${dim - e - t}V${dim - e}H${e + t}`,
    `M${dim - e - t} ${dim - e}H${dim - e}V${dim - e - t}`,
  ]
    .map((d) => `<path d="${d}" fill="none" stroke="${DIM}" stroke-width="1"/>`)
    .join("");
  const label = opts.label
    ? `<text x="${dim / 2}" y="${dim - 7}" fill="${DIM}" font-family="ui-monospace,monospace" font-size="6" text-anchor="middle">#${id.toString()}</text>`
    : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" width="${dim * 4}" height="${dim * 4}" shape-rendering="crispEdges"><rect width="${dim}" height="${dim}" fill="${PLATE}"/>${ticks}${cells}${label}</svg>`;
}

export function sigilDataUri(id: string | number | bigint): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(sigilSvg(id))}`;
}
